'use client';

/**
 * Catalog state for the marketplace skills picker: debounced search, the
 * filter axes, the fetch loop, and which row is selected.
 *
 * Extracted from ChatSkillsModal so the fetch loop can be exercised on its own.
 * Every fetch is abortable and the in-flight one is torn down when a filter
 * changes or the modal unmounts — otherwise a slow first response would land
 * after a faster later one and show stale results.
 */

import { useCallback, useEffect, useMemo, useState } from 'react';
import { fetchMeridianSkills } from './fetchMeridianSkills.js';
import type { MeridianSkillPickerItem } from './types.js';

export const ALL_CATEGORIES_KEY = 'all';
/** Search debounce, in ms. */
export const SEARCH_DEBOUNCE_MS = 200;
/** Server-side page size for the picker. */
export const SKILL_PAGE_SIZE = 100;

export interface SkillCatalogState {
  query: string;
  setQuery: (value: string) => void;
  category: string;
  setCategory: (value: string) => void;
  activatableOnly: boolean;
  toggleActivatableOnly: () => void;
  skills: MeridianSkillPickerItem[];
  categories: string[];
  syncHint: string | null;
  total: number;
  selectedId: string | null;
  setSelectedId: (id: string) => void;
  selected: MeridianSkillPickerItem | null;
  loading: boolean;
  error: string | null;
}

export function useSkillCatalog({
  apiBaseUrl,
  open,
}: {
  apiBaseUrl: string;
  open: boolean;
}): SkillCatalogState {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [category, setCategory] = useState(ALL_CATEGORIES_KEY);
  const [activatableOnly, setActivatableOnly] = useState(true);
  const [skills, setSkills] = useState<MeridianSkillPickerItem[]>([]);
  const [categories, setCategories] = useState<string[]>([]);
  const [syncHint, setSyncHint] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const t = window.setTimeout(() => setDebouncedQuery(query), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(t);
  }, [query]);

  useEffect(() => {
    if (!open) return;

    const controller = new AbortController();
    setLoading(true);
    setError(null);

    fetchMeridianSkills({
      apiBaseUrl,
      query: debouncedQuery,
      category: category === ALL_CATEGORIES_KEY ? undefined : category,
      activatableOnly,
      limit: SKILL_PAGE_SIZE,
      signal: controller.signal,
    })
      .then((result) => {
        setSkills(result.skills);
        setCategories(result.categories);
        setSyncHint(result.syncHint);
        setTotal(result.total);
        setSelectedId((prev) => {
          if (prev && result.skills.some((s) => s.id === prev)) return prev;
          return result.skills[0]?.id ?? null;
        });
      })
      .catch((err: unknown) => {
        if (controller.signal.aborted) return;
        setError(err instanceof Error ? err.message : 'Failed to load skills');
        setSkills([]);
        setTotal(0);
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });

    return () => controller.abort();
  }, [open, apiBaseUrl, debouncedQuery, category, activatableOnly]);

  // Reopening the picker starts from the default filter set rather than
  // whatever the user left behind last time.
  useEffect(() => {
    if (!open) {
      setQuery('');
      setDebouncedQuery('');
      setCategory(ALL_CATEGORIES_KEY);
      setActivatableOnly(true);
      setSelectedId(null);
      setError(null);
    }
  }, [open]);

  const selected = useMemo(
    () => skills.find((s) => s.id === selectedId) ?? null,
    [skills, selectedId]
  );

  const toggleActivatableOnly = useCallback(() => setActivatableOnly((v) => !v), []);
  const selectId = useCallback((id: string) => setSelectedId(id), []);

  return {
    query,
    setQuery,
    category,
    setCategory,
    activatableOnly,
    toggleActivatableOnly,
    skills,
    categories,
    syncHint,
    total,
    selectedId,
    setSelectedId: selectId,
    selected,
    loading,
    error,
  };
}
